import type { MetricDto, RuleTargetDto } from '@citilight-monitor/contracts';
import { Check, Pencil, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useRules, useStoredMetrics, useUpdateMetricLabel } from '../../lib/api';
import { useToast } from '../../lib/toast';
import { Button, EmptyState, Input } from '../atoms';
import { PopupTemplate } from '../templates';

interface TargetMetricsDialogProps {
  open: boolean;
  onClose: () => void;
  target?: RuleTargetDto;
}

/** The raw identity behind a label, e.g. "overview_online_devices (project=NDMC)" - what actually tells two same-named metrics apart. */
function describeMetricIdentity(metric: MetricDto): string {
  if (!metric.labels || Object.keys(metric.labels).length === 0) return metric.source;
  const labels = Object.entries(metric.labels)
    .map(([key, value]) => `${key}=${value}`)
    .join(', ');
  return `${metric.source} (${labels})`;
}

export function TargetMetricsDialog({ open, onClose, target }: TargetMetricsDialogProps) {
  const metricsQuery = useStoredMetrics(open ? target?.id : undefined);
  const rulesQuery = useRules(target?.id);
  const updateLabel = useUpdateMetricLabel();
  const { showSuccess } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (!open) setEditingId(null);
  }, [open]);

  function startEdit(id: string, currentLabel: string) {
    setEditingId(id);
    setDraft(currentLabel);
  }

  async function save(metricId: string) {
    const label = draft.trim();
    if (!target || !label) return;
    await updateLabel.mutateAsync({ ruleTargetId: target.id, metricId, label });
    setEditingId(null);
    showSuccess('Metric label updated.');
  }

  const usedKeys = useMemo(() => new Set((rulesQuery.data ?? []).map((rule) => rule.metricKey)), [rulesQuery.data]);
  const metrics = (metricsQuery.data ?? []).filter((metric) => usedKeys.has(metric.key));

  return (
    <PopupTemplate
      open={open}
      onClose={onClose}
      title="Metrics"
      description={target ? `Rename how ${target.name}'s metrics appear in rules and alerts.` : undefined}
    >
      <div className="flex flex-col gap-2">
        {metricsQuery.isPending || rulesQuery.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : metricsQuery.error || rulesQuery.error ? (
          <EmptyState>Could not load metrics: {(metricsQuery.error ?? rulesQuery.error)?.message}</EmptyState>
        ) : metrics.length === 0 ? (
          <EmptyState>No rules use a metric on this target yet. Create a rule to see its metric here.</EmptyState>
        ) : (
          metrics.map((metric) => (
            <div key={metric.id} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2">
              <div className="min-w-0 flex-1">
                {editingId === metric.id ? (
                  <Input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void save(metric.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                ) : (
                  <div className="truncate text-[13px] font-medium text-fg">{metric.label}</div>
                )}
                <div className="truncate font-mono text-[11px] text-fg-muted">{describeMetricIdentity(metric)}</div>
              </div>
              {editingId === metric.id ? (
                <div className="flex flex-none gap-1">
                  <Button variant="ghost" size="icon" aria-label="Save" onClick={() => void save(metric.id)} icon={<Check size={14} aria-hidden />} />
                  <Button variant="ghost" size="icon" aria-label="Cancel" onClick={() => setEditingId(null)} icon={<X size={14} aria-hidden />} />
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Rename"
                  onClick={() => startEdit(metric.id, metric.label)}
                  icon={<Pencil size={14} aria-hidden />}
                />
              )}
            </div>
          ))
        )}
      </div>
    </PopupTemplate>
  );
}
